/* eslint-disable import/prefer-default-export */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@src/store/reduxstore';
import { CallStatus } from '@src/types/calls';
import { activeChatIdSelector, activeChatSelector } from './chats';

const callsState = (state: RootState) => state.calls;

export const selectActiveCallId = createSelector(
  callsState,
  state => state.activeCallId
);

export const selectActiveCall = createSelector(
  callsState,
  ({ calls, activeCallId }) => calls.find(call => call.id === activeCallId)
);

export const selectAnsweringCallId = (state: RootState) =>
  state.calls.answeringCallId;

// Whether this agent ended the call (hung up, or a terminal event arrived). Async work
// for it that is still running, or that a late event starts, must stop.
export const selectIsCallEnded = (callId: string) => (state: RootState) =>
  !!state.calls.endedCallIds?.includes(callId);

// Whether an incoming call can be answered now: it is still waiting for an agent, and
// no answer for it is running or already live.
export const selectCanAnswerCall = (callId: string) => (state: RootState) => {
  const { calls, activeCallId, answeringCallId } = state.calls;
  if (answeringCallId === callId || activeCallId === callId) return false;
  return calls.some(
    call => call.id === callId && call.status === CallStatus.WAITING_AGENT
  );
};

export const selectCallById = (callId: string) =>
  createSelector(callsState, ({ calls }) =>
    calls.find(call => call.id === callId)
  );

// eslint-disable-next-line import/export
export const selectCallByChatId = (chatId: string) =>
  createSelector(callsState, ({ calls }) => {
    const callsFound = calls.filter(call => call.chatId === chatId);
    return (
      callsFound?.find(call => call.status !== CallStatus.MISSED) ??
      callsFound?.at(-1)
    );
  });

export const selectFirstCall = createSelector(callsState, ({ calls }) =>
  calls?.find(call =>
    [
      CallStatus.ONGOING,
      CallStatus.WAITING_AGENT,
      CallStatus.WAITING_ANSWER,
    ].includes(call.status)
  )
);

export const selectPreferredCall = (state: RootState) => {
  const activeChat = activeChatSelector(state);
  const { calls, activeCallId } = state.calls;

  const availableCallStatus = [
    CallStatus.ONGOING,
    CallStatus.WAITING_AGENT,
    CallStatus.WAITING_ANSWER,
  ];

  const availableCalls = calls.filter(call =>
    availableCallStatus.includes(call.status)
  );

  // eslint-disable-next-line no-restricted-syntax
  for (const call of availableCalls) {
    if (call.id === activeCallId) return call;
    if (call.chatId === activeChat.id) return call;
  }

  return undefined;
};

export const selectHasOngoingCall = (state: RootState) =>
  Boolean(state.calls.activeCallId) ||
  state.calls.calls?.some(call => call.status === CallStatus.ONGOING);

export const selectHasWaitingCall = (state: RootState) =>
  state.calls.calls?.some(call =>
    [CallStatus.WAITING_AGENT, CallStatus.WAITING_ANSWER].includes(call.status)
  );

export const selectHasCallInActiveChat = (state: RootState) => {
  const activeChatId = activeChatIdSelector(state);

  const call = selectCallByChatId(activeChatId)(state);
  return (
    call?.status &&
    [
      CallStatus.WAITING_AGENT,
      CallStatus.WAITING_ANSWER,
      CallStatus.ONGOING,
    ].includes(call?.status)
  );
};

export const selectHasOngoingCallInActiveChat = (state: RootState) => {
  const activeChatId = activeChatIdSelector(state);

  const call = selectCallByChatId(activeChatId)(state);
  return call?.status === CallStatus.ONGOING;
};

export const selectHasWaitingAgentCall = (state: RootState) =>
  state.calls.calls?.some(call =>
    [CallStatus.WAITING_AGENT].includes(call.status)
  );

export const selectHasWaitingAnswerCall = (state: RootState) =>
  state.calls.calls?.some(call =>
    [CallStatus.WAITING_ANSWER].includes(call.status)
  );

export const hasWaitingOrOngoingCall = (state: RootState) =>
  state.calls?.calls?.some(call =>
    [CallStatus.WAITING_ANSWER, CallStatus.ONGOING].includes(call.status)
  );
